import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#applySearchContext();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  #applySearchContext() {
    const params = new URLSearchParams(window.location.search);
    const contextType = params.get('search_context_type');
    const contextHandle = params.get('search_context_handle');
    const isCatalogContext = ['collection_link', 'all_products_link', 'catalog_link'].includes(contextType || '');

    if (!isCatalogContext) return;

    const cards = Array.from(this.querySelectorAll('[data-product-collections]'));
    if (cards.length === 0) return;

    let hasVisibleCards = false;

    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const collectionHandles = (card.dataset.productCollections || '')
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean);

      const isVisible =
        contextType === 'collection_link' && contextHandle ? collectionHandles.includes(contextHandle) : true;
      card.hidden = !isVisible;

      if (isVisible) {
        hasVisibleCards = true;
      }
    });

    const title = this.querySelector('.main-collection-grid__title');
    if (title instanceof HTMLElement) {
      const contextTitle = params.get('search_context_title');
      if (contextTitle) {
        title.textContent = `${title.textContent} · ${contextTitle}`;
      }
    }

    if (!hasVisibleCards) {
      const grid = this.querySelector('[data-testid="product-grid"]');
      if (grid instanceof HTMLElement && !this.querySelector('.search-context-empty')) {
        grid.insertAdjacentHTML(
          'afterend',
          '<p class="search-context-empty">No se encontraron productos en esta seccion para tu busqueda.</p>'
        );
      }
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
